import React from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ScreenshotLangPicker from "../../../components/ScreenshotLangPicker";
import {
    appendScreenshotLang,
    screenshotSceneKey,
    useScreenshotTranslation,
} from "../../../lib/screenshotI18n";
import {
    formatScreenshotAccessExpiredLabel,
    formatScreenshotAccessValidThroughLabel,
    isScreenshotAccessExpired,
} from "../lib/screenshotAccess";
import { MAIN_PATH, SCREENSHOTS_MAIN_PATH, screenshotPath } from "../routes";
import {
    isScreenshotSceneConfigured,
    WORD_WHEEL_SCREENSHOT_SCENES,
} from "../screenshotScenes";
import { phoneShellSx, wordWheelScreenshotAccent } from "../styles/screenshotStyles";

/**
 * App Store screenshot index — one link per configured Word Wheel scene (01–08).
 */
export default function WordWheelScreenshotIndexPage() {
    const { t, lang } = useScreenshotTranslation();
    const expired = isScreenshotAccessExpired();

    return (
        <Box sx={{ ...phoneShellSx, px: 2, py: 3 }}>
            <ScreenshotLangPicker />

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("mobileScreenshot.wordWheel.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("mobileScreenshot.common.index-intro")}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mb: 3 }}>
                <Box
                    component="span"
                    sx={{ color: "error.main", fontWeight: 600 }}
                >
                    {expired
                        ? t("mobileScreenshot.common.index-expired-on", {
                              date: formatScreenshotAccessExpiredLabel(),
                          })
                        : t("mobileScreenshot.common.index-available-through", {
                              date: formatScreenshotAccessValidThroughLabel(),
                          })}
                </Box>
            </Typography>
            <Stack spacing={1.5}>
                {WORD_WHEEL_SCREENSHOT_SCENES.map((scene) => {
                    const label = t(screenshotSceneKey("wordWheel", scene.slug, "label"));
                    const configured = isScreenshotSceneConfigured(scene);
                    if (expired) {
                        return (
                            <Typography
                                key={scene.slug}
                                variant="body2"
                                sx={{ color: "text.disabled", fontWeight: 600 }}
                            >
                                {t("mobileScreenshot.common.scene-link", { slug: scene.slug, label })}{" "}
                                {t("mobileScreenshot.common.scene-expired")}
                            </Typography>
                        );
                    }
                    if (!configured) {
                        return (
                            <Typography
                                key={scene.slug}
                                variant="body2"
                                sx={{ color: "text.disabled", fontWeight: 600 }}
                            >
                                {t("mobileScreenshot.common.scene-link", { slug: scene.slug, label })}{" "}
                                {t("mobileScreenshot.common.scene-add-puzzle")}
                            </Typography>
                        );
                    }
                    return (
                        <Link
                            key={scene.slug}
                            component={RouterLink}
                            to={appendScreenshotLang(screenshotPath(scene.slug), lang)}
                            underline="hover"
                            sx={{ color: wordWheelScreenshotAccent, fontWeight: 600 }}
                        >
                            {t("mobileScreenshot.common.scene-link", { slug: scene.slug, label })}
                        </Link>
                    );
                })}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3 }}>
                {t("mobileScreenshot.common.wireframe-app")}{" "}
                <Link component={RouterLink} to={MAIN_PATH}>
                    {MAIN_PATH}
                </Link>
                {" · "}
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH}>
                    {SCREENSHOTS_MAIN_PATH}
                </Link>
            </Typography>
        </Box>
    );
}
